import React, { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { get, post } from "../lib/http.js";
import { API, BASE_URL } from "../config/api.js";

const inr = (n) =>
  typeof n === "number" && !Number.isNaN(n)
    ? `₹${n.toLocaleString("en-IN")}`
    : n || "—";

/** Strip HTML for safe one-line / card previews (no raw <p> tags). */
function plainTextFromHtml(s) {
  if (!s || typeof s !== "string") return "";
  const noTags = s.replace(/<[^>]*>/g, " ");
  return noTags
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/g, "&")
    .replace(/&lt;/g, "<")
    .replace(/&gt;/g, ">")
    .replace(/&quot;/g, '"')
    .replace(/\s+/g, " ")
    .trim();
}

function absoluteMediaUrl(maybeRelative) {
  const u = String(maybeRelative || "").trim();
  if (!u) return "";
  if (/^https?:\/\//i.test(u)) return u;
  const origin = BASE_URL.replace(/\/+$/, "");
  return u.startsWith("/") ? `${origin}${u}` : `${origin}/${u}`;
}

function getEventImageUrl(event) {
  const primary = absoluteMediaUrl(event?.imageUrl || event?.image);
  if (primary) return primary;
  const land = event?.landsacpImage;
  if (Array.isArray(land) && land.length) {
    const first = land.find(Boolean);
    if (first) return absoluteMediaUrl(first);
  }
  if (typeof land === "string" && land.trim())
    return absoluteMediaUrl(land);
  if (event?.layoutImage)
    return absoluteMediaUrl(event.layoutImage);
  return "";
}

function formatEventNoTZ(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const y = d.getUTCFullYear();
  const m = d.getUTCMonth();
  const day = d.getUTCDate();
  const hh = d.getUTCHours();
  const mm = d.getUTCMinutes();
  const dateOnly = new Date(Date.UTC(y, m, day));
  const timeStr = `${((hh % 12) || 12)}:${String(mm).padStart(2, "0")} ${
    hh < 12 ? "AM" : "PM"
  }`;
  const dateStr = dateOnly.toLocaleDateString("en-IN", {
    weekday: "short",
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
  return `${dateStr} · ${timeStr}`;
}

function minTicketPrice(event) {
  const cats = event?.ticketCategories;
  if (!Array.isArray(cats) || !cats.length) return null;
  const prices = cats.map((t) => t?.price).filter((p) => typeof p === "number");
  if (!prices.length) return null;
  return Math.min(...prices);
}

function HeartFilled({ className = "h-5 w-5" }) {
  return (
    <svg
      className={`${className} text-rose-500`}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden
    >
      <path d="M12.001 21.35l-1.451-1.319C5.401 15.36 2 12.28 2 8.5 2 6 4 4 6.5 4c1.74 0 3.41 1.01 4.221 2.09.364.43 1.012.43 1.376 0C14.089 5.01 15.76 4 17.5 4 20 4 22 6 22 8.5c0 3.78-3.401 6.86-8.55 11.54L12.001 21.35z" />
    </svg>
  );
}

function RemoveIcon({ className = "h-4 w-4" }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden
    >
      <line x1="18" y1="6" x2="6" y2="18" />
      <line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  );
}

function ImagePlaceholder({ title }) {
  return (
    <div
      className="flex h-full w-full flex-col items-center justify-center bg-gradient-to-br from-amber-100 via-orange-50 to-slate-100 text-slate-500"
      role="img"
      aria-label={title ? `No poster for ${title}` : "No poster"}
    >
      <svg
        className="mb-2 h-10 w-10 text-amber-400/90"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="1.5"
      >
        <rect x="3" y="5" width="18" height="14" rx="2" />
        <circle cx="8.5" cy="10" r="1.5" fill="currentColor" stroke="none" />
        <path d="M21 15l-5-5-4 4-2-2-4 4" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
      <span className="px-3 text-center text-xs font-medium text-slate-600">
        {title ? "Poster unavailable" : "No image"}
      </span>
    </div>
  );
}

function WishlistEventCard({ event, onRemove }) {
  const [imgFailed, setImgFailed] = useState(false);
  const id = event._id || event.id;
  const title = event.name || event.title || "Event";
  const descRaw = event.description || event.about || "";
  const desc = plainTextFromHtml(descRaw) || "Explore this event for full details.";
  const imgSrc = getEventImageUrl(event);
  const showImg = Boolean(imgSrc) && !imgFailed;
  const minPrice = minTicketPrice(event);
  const dateLine = formatEventNoTZ(event.date);
  const venueLine = [event?.venue?.name, event?.venue?.city].filter(Boolean).join(" · ");

  return (
    <article className="group flex flex-col overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-sm transition hover:border-amber-200/80 hover:shadow-md dark:border-slate-700 dark:bg-slate-900">
      <div className="relative aspect-[16/10] w-full shrink-0 overflow-hidden bg-slate-100 dark:bg-slate-800">
        {showImg ? (
          <img
            src={imgSrc}
            alt=""
            className="h-full w-full object-cover transition duration-300 group-hover:scale-[1.02]"
            loading="lazy"
            onError={() => setImgFailed(true)}
          />
        ) : (
          <ImagePlaceholder title={title} />
        )}
        <div className="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/25 to-transparent opacity-0 transition group-hover:opacity-100" />
        <button
          type="button"
          onClick={() => onRemove(id)}
          className="absolute right-2.5 top-2.5 inline-flex h-9 w-9 items-center justify-center rounded-full bg-white/95 text-rose-600 shadow-md ring-1 ring-black/5 transition hover:bg-rose-50 hover:text-rose-700 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-400"
          aria-label={`Remove ${title} from wishlist`}
        >
          <RemoveIcon />
        </button>
        {event.category ? (
          <span className="absolute bottom-2.5 left-2.5 rounded-full bg-white/95 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-amber-800 shadow-sm ring-1 ring-black/5">
            {event.category}
          </span>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col p-4">
        <h2 className="line-clamp-2 font-sans text-base font-semibold leading-snug text-slate-900 dark:text-white">
          {title}
        </h2>
        {venueLine ? (
          <p className="mt-1 line-clamp-1 font-sans text-xs text-slate-600 dark:text-slate-400">
            {venueLine}
          </p>
        ) : null}
        {dateLine ? (
          <p className="mt-1 font-sans text-xs font-medium text-slate-700 dark:text-slate-300">
            {dateLine}
          </p>
        ) : null}
        <p className="mt-2 line-clamp-2 font-sans text-sm leading-relaxed text-slate-600 dark:text-slate-400">
          {desc}
        </p>

        <div className="mt-4 flex flex-wrap items-end justify-between gap-3 border-t border-slate-100 pt-3 dark:border-slate-800">
          <div>
            <div className="font-sans text-[10px] font-semibold uppercase tracking-wide text-slate-500">
              From
            </div>
            <div className="font-sans text-lg font-bold text-amber-600 dark:text-amber-400">
              {minPrice === null
                ? "—"
                : minPrice === 0
                  ? "Free"
                  : inr(minPrice)}
            </div>
          </div>
          <Link
            to={`/events/${id}`}
            className="inline-flex shrink-0 items-center justify-center rounded-xl bg-amber-400 px-4 py-2 font-sans text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-amber-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-amber-500 focus-visible:ring-offset-2 dark:ring-offset-slate-900"
          >
            View details
          </Link>
        </div>
      </div>
    </article>
  );
}

export default function WishlistPage() {
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchWishlist = async () => {
      try {
        const raw = localStorage.getItem("bb_user");
        const parsed = raw ? JSON.parse(raw) : null;
        const userId = parsed?.id;
        const token = parsed?.token;

        if (!userId || !token) {
          setWishlist([]);
          return;
        }

        const res = await get(`${API.wishlist.getWishlist}/${userId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });

        const wishlistItems = (res?.wishlist?.eventIds || []).filter(Boolean);
        setWishlist(wishlistItems);
      } catch (err) {
        console.error("Error fetching wishlist:", err);
        setWishlist([]);
      } finally {
        setLoading(false);
      }
    };

    fetchWishlist();
  }, []);

  const handleRemove = useCallback(async (eventId) => {
    try {
      const raw = localStorage.getItem("bb_user");
      const { id: userId, token } = JSON.parse(raw) || {};
      if (!userId || !token) return;

      await post(
        `${API.wishlist.removeFromWishlist}`,
        { userId, eventId },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setWishlist((prev) =>
        prev.filter((e) => String(e._id || e.id) !== String(eventId))
      );
    } catch (err) {
      console.error("Remove from wishlist failed:", err);
    }
  }, []);

  if (loading) {
    return (
      <div className="font-sans antialiased">
        <div className="mx-auto max-w-5xl px-4 py-16 text-center">
          <div className="mx-auto h-10 w-10 animate-spin rounded-full border-2 border-amber-400 border-t-transparent" />
          <p className="mt-4 text-sm text-slate-600 dark:text-slate-400">
            Loading your wishlist…
          </p>
        </div>
      </div>
    );
  }

  if (!wishlist.length) {
    return (
      <div className="font-sans antialiased">
        <div className="mx-auto max-w-lg px-4 py-16 text-center">
          <HeartFilled className="mx-auto mb-4 h-14 w-14 opacity-90" />
          <h2 className="text-xl font-semibold text-slate-800 dark:text-slate-100">
            Your wishlist is empty
          </h2>
          <p className="mt-2 text-sm text-slate-600 dark:text-slate-400">
            Save events you love — they will show up here.
          </p>
          <Link
            to="/events"
            className="mt-8 inline-flex rounded-xl bg-amber-400 px-6 py-3 text-sm font-semibold text-slate-900 shadow-sm transition hover:bg-amber-300"
          >
            Browse events
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="font-sans antialiased">
      <div className="mx-auto max-w-6xl px-4 py-8 sm:py-10">
        <header className="mb-8 rounded-2xl border border-amber-100/80 bg-gradient-to-r from-amber-50 via-white to-orange-50/90 px-5 py-6 shadow-sm dark:border-amber-900/30 dark:from-slate-900 dark:via-slate-900 dark:to-slate-800">
          <div className="flex flex-wrap items-center gap-3">
            <HeartFilled className="h-8 w-8 shrink-0" />
            <div>
              <h1 className="text-xl font-bold tracking-tight text-slate-900 dark:text-white sm:text-2xl">
                My wishlist
              </h1>
              <p className="mt-0.5 text-sm text-slate-600 dark:text-slate-400">
                {wishlist.length} saved {wishlist.length === 1 ? "event" : "events"}
              </p>
            </div>
          </div>
        </header>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {wishlist.map((event) => (
            <WishlistEventCard
              key={String(event._id || event.id)}
              event={event}
              onRemove={handleRemove}
            />
          ))}
        </div>
      </div>
    </div>
  );
}
